import { createServerFn } from "@tanstack/react-start";
import { z } from "zod";
import { requireSupabaseAuth } from "@/integrations/supabase/auth-middleware";
import { APP_LOCALES, toAppLocale, type AppLocale } from "@/lib/locale";

export const getMyProfile = createServerFn({ method: "GET" })
  .middleware([requireSupabaseAuth])
  .handler(async ({ context }) => {
    const { supabase, userId } = context;
    const [{ data: profile, error: profileErr }, { data: lastBooking }] = await Promise.all([
      supabase
        .from("profiles")
        .select("full_name, phone, email, preferred_locale")
        .eq("id", userId)
        .maybeSingle(),
      supabase
        .from("bookings")
        .select("student_name, phone")
        .eq("user_id", userId)
        .order("created_at", { ascending: false })
        .limit(1)
        .maybeSingle(),
    ]);
    if (profileErr) throw new Error(profileErr.message);
    return {
      full_name: profile?.full_name ?? lastBooking?.student_name ?? null,
      phone: profile?.phone ?? lastBooking?.phone ?? null,
      email: profile?.email ?? null,
      preferred_locale: toAppLocale(profile?.preferred_locale),
    };
  });

const PreferredLocaleSchema = z.object({
  locale: z.enum(APP_LOCALES),
});

export const updateMyPreferredLocale = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .validator((d: unknown) => PreferredLocaleSchema.parse(d))
  .handler(async ({ context, data }) => {
    const { supabase, userId } = context;
    const locale = data.locale as AppLocale;
    const { error } = await supabase
      .from("profiles")
      .update({ preferred_locale: locale })
      .eq("id", userId);
    if (error) throw new Error(error.message);
    return { preferred_locale: locale };
  });
